// Translation
import { useTrans } from "@/i18n"

// UI
import { Card } from "@/components/Card"
import { Heading } from "@/components/Typography"

// Specific UI
import { DocumentsTable } from "./DocumentsTable"

// Icons
import { CheckIcon } from "@/lib/icons"

// Context
import { useLang } from "@/context/useLang"

// API
import {
	RiskKycStateChoices,
	type KycVerificationDocumentType,
} from "@/api/graphql"

// DateTime
import { DateTime } from "@/lib/dates"
import { dateFormat } from "@/constants/constants"

/**
 * StepCompletedOverlay
 * @param param0
 * @returns
 */
export const StepCompletedOverlay = ({
	state,
	message,
	last_verified_on,
	documents,
}: {
	state: string | undefined
	message?: string | null
	last_verified_on?: string
	documents?: KycVerificationDocumentType[] | null
}) => {
	// i18n
	const t = useTrans("investor")
	const { formatDate } = useLang()

	// When no state is provided, return null
	if (state === undefined) {
		return null
	}

	// Template
	return (
		<div
			className="absolute inset-0 z-10 bg-gray-600/50 p-5 text-center text-lg"
			data-testid="StepCompletedOverlay"
		>
			<Card className="mx-auto max-w-md">
				<div className="space-y-4 text-center">
					{/** State */}
					<Heading
						as="h2"
						styleAs="h5"
						className="flex justify-center gap-2"
					>
						<div className="flex items-center">
							<CheckIcon
								aria-hidden="true"
								className="size-5 text-lg text-green-500"
							/>
						</div>
						{t(`investor:identity.status.${state}`)}
					</Heading>

					{/** Show human readable message */}
					{message && <p className="text-gray-500">{message}</p>}

					{/** When succesfully verified */}
					{RiskKycStateChoices.VerificationSuccess &&
						last_verified_on && (
							<div>
								{DateTime.fromISO(last_verified_on).toFormat(
									dateFormat,
								)}
							</div>
						)}

					{/** Documents */}
					{documents && documents.length > 0 && (
						<>
							{/** Show remaining time until expiry date */}
							{documents[0]?.expiry_date ? (
								<p className="font-bold">
									{t(
										"investor:identity.generic.expiry-date",
										{
											days: formatDate(
												DateTime.fromISO(
													documents[0].expiry_date,
												),
												{
													variant: "verbose",
												},
											),
										},
									)}
								</p>
							) : null}

							{/** Show documents */}
							<DocumentsTable documents={documents} />
						</>
					)}
				</div>
			</Card>
		</div>
	)
}
